<template>
  <section id="top-products-ranking-outdated" class="flex flex-col gap-3 overflow-hidden card">
    <MessageBanner
      type="reminder"
      inline="top"
      class="-mx-6 -mt-6 w-auto"
      :headline="t('ProductTop100Ranking.countdown.title')"
      data-testid="top-products-ranking-motivation"
    >
      <i18n-t v-if="daysLeft > 0" keypath="ProductTop100Ranking.countdown.pageBody" tag="p" class="text-body-m" scope="global">
        <template #days>
          <strong data-testid="top-products-ranking-motivation-days">{{ t('ProductTop100Ranking.countdown.days', daysLeft) }}</strong>
        </template>
      </i18n-t>
      <p v-else class="text-body-m">{{ t('ProductTop100Ranking.countdown.lastDay') }}</p>
    </MessageBanner>
    <p v-if="loading" class="txt-body-s-regular text-subtle" data-testid="top-products-ranking-outdated-loading">
      {{ t('ProductTop100Ranking.outdated.loading') }}
    </p>
    <template v-else-if="error">
      <h2 class="txt-headline-l-bold">{{ t('ProductTop100Ranking.outdated.title') }}</h2>
      <p class="txt-body-s-regular text-subtle" data-testid="top-products-ranking-outdated-unavailable">
        {{ t('ProductTop100Ranking.outdated.unavailable') }}
      </p>
      <RouterLink
        class="btn-primary-color-m self-start"
        data-testid="top-products-ranking-outdated-cta"
        :to="outdatedReviewsRoute"
        @click="trackCta"
      >
        {{ t('ProductTop100Ranking.outdated.cta') }}
      </RouterLink>
    </template>
    <template v-else-if="outdatedCount > 0">
      <h2 class="txt-headline-l-bold" data-testid="top-products-ranking-outdated">
        {{ t('ProductTop100Ranking.outdated.heading', outdatedCount) }}
      </h2>
      <p class="max-w-prose txt-body-s-regular text-subtle">{{ t('ProductTop100Ranking.outdated.hint') }}</p>
      <RouterLink
        class="btn-primary-color-m self-start"
        data-testid="top-products-ranking-outdated-cta"
        :to="outdatedReviewsRoute"
        @click="trackCta"
      >
        {{ t('ProductTop100Ranking.outdated.cta') }}
      </RouterLink>
    </template>
    <template v-else>
      <h2 class="txt-headline-l-bold" data-testid="top-products-ranking-outdated-none">{{ t('ProductTop100Ranking.outdated.none') }}</h2>
      <p class="max-w-prose txt-body-s-regular text-subtle" data-testid="top-products-ranking-outdated-none-hint">
        {{ t('ProductTop100Ranking.outdated.noneHint') }}
      </p>
    </template>
  </section>
</template>
<script setup lang="ts">
import { MessageBanner } from '@ramp106/omrjs-core-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, toRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { SurveyResponsesViewEnum } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'

import { daysUntilCollectionDeadline } from './collectionDeadline'
import { useOutdatedReviewCount } from './useOutdatedReviewCount'

function trackCta() {
  track('cta_click', { surface: 'cta', category: 'meaningful_action', cta_id: 'top_products_ranking_outdated' })
}

const props = defineProps<{
  productId: string
  productSlug: string
  now?: Date
}>()

const daysLeft = computed(() => daysUntilCollectionDeadline(props.now ?? new Date()))

const { t } = useI18n()
const { outdatedCount, loading, error } = useOutdatedReviewCount(toRef(props, 'productSlug'))

const outdatedReviewsRoute = computed(() => ({
  name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns,
  params: { productId: props.productId },
  query: { view: SurveyResponsesViewEnum.Outdated },
}))
</script>
